import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { TrendingUp, TrendingDown, Package, AlertTriangle, CheckCircle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Sale {
  id: string;
  sale_date: string;
  total_amount: number;
  quantity_sold: number;
  product_id: string;
  product_name: string;
  unit_price: number;
  payment_method: string;
}

interface Product {
  id: string;
  name: string;
  quantity: number;
  min_threshold: number;
  price: number;
}

interface ProductDemandAnalysisProps {
  sales: Sale[];
  products: Product[];
  customColor?: string;
}

interface ProductDemand {
  product: Product;
  totalSold: number;
  totalRevenue: number;
  demandScore: number;
  stockStatus: 'critical' | 'low' | 'normal' | 'high';
  stockPercentage: number;
}

export const ProductDemandAnalysis: React.FC<ProductDemandAnalysisProps> = ({
  sales,
  products,
  customColor
}) => {
  const formatPrice = (price: number) => new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR'
  }).format(price);

  const productAnalysis = useMemo(() => {
    // Calculer les ventes par produit
    const salesByProduct = sales.reduce((acc, sale) => {
      const productId = sale.product_id;
      if (!acc[productId]) {
        acc[productId] = {
          totalSold: 0,
          totalRevenue: 0,
          salesCount: 0
        };
      }
      acc[productId].totalSold += sale.quantity_sold;
      acc[productId].totalRevenue += sale.total_amount;
      acc[productId].salesCount += 1;
      return acc;
    }, {} as Record<string, { totalSold: number; totalRevenue: number; salesCount: number }>);

    // Créer l'analyse de demande pour chaque produit
    const productDemands: ProductDemand[] = products.map(product => {
      const salesData = salesByProduct[product.id] || { totalSold: 0, totalRevenue: 0, salesCount: 0 };
      
      // Score de demande basé sur la quantité vendue et la fréquence des ventes
      const demandScore = salesData.totalSold + (salesData.salesCount * 2);
      
      // Statut du stock
      const stockPercentage = product.min_threshold > 0 ? (product.quantity / product.min_threshold) * 100 : 100;
      let stockStatus: 'critical' | 'low' | 'normal' | 'high';
      
      if (product.quantity === 0) {
        stockStatus = 'critical';
      } else if (product.quantity <= product.min_threshold) {
        stockStatus = 'low';
      } else if (stockPercentage < 200) {
        stockStatus = 'normal';
      } else {
        stockStatus = 'high';
      }

      return {
        product,
        totalSold: salesData.totalSold,
        totalRevenue: salesData.totalRevenue,
        demandScore,
        stockStatus,
        stockPercentage: Math.min(stockPercentage, 200) // Limité à 200% pour l'affichage
      };
    });

    // Trier par score de demande
    productDemands.sort((a, b) => b.demandScore - a.demandScore);

    const mostInDemand = productDemands.slice(0, 5);
    const leastInDemand = productDemands.slice(-5).reverse();

    return { mostInDemand, leastInDemand, allProducts: productDemands };
  }, [sales, products]);

  const getStockIcon = (status: string) => {
    switch (status) {
      case 'critical':
        return <AlertTriangle className="w-4 h-4 text-red-600" />;
      case 'low':
        return <AlertTriangle className="w-4 h-4 text-orange-500" />;
      case 'normal':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'high':
        return <Package className="w-4 h-4 text-blue-600" />;
      default:
        return <Package className="w-4 h-4 text-muted-foreground" />;
    }
  };

  const getStockColor = (status: string) => {
    switch (status) {
      case 'critical':
        return 'bg-red-500';
      case 'low':
        return 'bg-orange-500';
      case 'normal':
        return 'bg-green-500';
      case 'high':
        return 'bg-blue-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStockLabel = (status: string) => {
    switch (status) {
      case 'critical':
        return 'Rupture';
      case 'low':
        return 'Stock faible';
      case 'normal':
        return 'Stock normal';
      case 'high':
        return 'Stock élevé';
      default:
        return 'Inconnu';
    }
  };

  const ProductCard: React.FC<{ productDemand: ProductDemand; rank: number; type: 'high' | 'low' }> = ({ 
    productDemand, 
    rank, 
    type 
  }) => (
    <div className="flex items-center justify-between p-3 border rounded-lg">
      <div className="flex items-center gap-3 flex-1">
        <div className={cn(
          "flex items-center justify-center w-8 h-8 rounded-full text-white text-sm font-bold",
          type === 'high' ? 'bg-green-600' : 'bg-red-600'
        )}>
          {rank}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="font-medium text-sm truncate">{productDemand.product.name}</h4>
            <Badge variant="outline" className="text-xs">
              {productDemand.totalSold} vendus
            </Badge>
          </div>
          
          <div className="flex items-center gap-2 mb-2">
            {getStockIcon(productDemand.stockStatus)}
            <span className="text-xs text-muted-foreground">
              Stock: {productDemand.product.quantity} unités
            </span>
            <Badge 
              variant="secondary" 
              className={cn("text-xs", 
                productDemand.stockStatus === 'critical' && 'bg-red-100 text-red-800',
                productDemand.stockStatus === 'low' && 'bg-orange-100 text-orange-800',
                productDemand.stockStatus === 'normal' && 'bg-green-100 text-green-800',
                productDemand.stockStatus === 'high' && 'bg-blue-100 text-blue-800'
              )}
            >
              {getStockLabel(productDemand.stockStatus)}
            </Badge>
          </div>
          
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Niveau de stock</span>
              <span>{Math.round(productDemand.stockPercentage)}%</span>
            </div>
            <Progress 
              value={productDemand.stockPercentage} 
              className="h-2"
              style={{ 
                backgroundColor: 'hsl(var(--muted))',
              }}
            />
          </div>
        </div>
        
        <div className="text-right">
          <div className="text-sm font-medium">
            {formatPrice(productDemand.totalRevenue)}
          </div>
          <div className="text-xs text-muted-foreground">
            CA total
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Produits les plus demandés */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp 
                className="w-5 h-5" 
                style={customColor ? { color: customColor } : undefined}
              />
              <span style={customColor ? { color: customColor } : undefined}>
                Top 5 - Produits les plus sollicités
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {productAnalysis.mostInDemand.length > 0 ? (
              productAnalysis.mostInDemand.map((productDemand, index) => (
                <ProductCard 
                  key={productDemand.product.id}
                  productDemand={productDemand}
                  rank={index + 1}
                  type="high"
                />
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <TrendingUp className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-sm">Aucune donnée de vente disponible</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Produits les moins demandés */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingDown className="w-5 h-5 text-red-600" />
              <span className="text-red-600">
                Top 5 - Produits les moins sollicités
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {productAnalysis.leastInDemand.length > 0 ? (
              productAnalysis.leastInDemand.map((productDemand, index) => (
                <ProductCard 
                  key={productDemand.product.id}
                  productDemand={productDemand}
                  rank={index + 1}
                  type="low"
                />
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <TrendingDown className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-sm">Aucune donnée disponible</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Résumé statistique */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            Résumé de l'analyse de demande
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {productAnalysis.allProducts.filter(p => p.totalSold > 0).length}
              </div>
              <div className="text-xs text-muted-foreground">Produits vendus</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {productAnalysis.allProducts.filter(p => p.stockStatus === 'critical').length}
              </div>
              <div className="text-xs text-muted-foreground">En rupture</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-500">
                {productAnalysis.allProducts.filter(p => p.stockStatus === 'low').length}
              </div>
              <div className="text-xs text-muted-foreground">Stock faible</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {productAnalysis.allProducts.filter(p => p.totalSold === 0).length}
              </div>
              <div className="text-xs text-muted-foreground">Jamais vendus</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};